import { useMemo } from 'react';
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { EnterpriseInquiryListParams, EnterpriseInquiryListResponse } from '@src/models/enterprise-inquiry';
import { planInquiryApi } from '@src/apis/admin';
import type { PlanInquiryStatus } from '@uniquegood/realworld-admin-interface';

export type PlanInquiryListFetcher = (params: EnterpriseInquiryListParams) => Promise<EnterpriseInquiryListResponse>;

type PlanInquiryListQueryKey = readonly ['plan-inquiries', EnterpriseInquiryListParams];

export interface UsePlanInquiryListOptions {
  /**
   * React Query 옵션을 덮어씁니다.
   * `queryKey`와 `queryFn`은 내부에서 관리하므로 제외했습니다.
   */
  queryOptions?: Omit<
    UseQueryOptions<EnterpriseInquiryListResponse, Error, EnterpriseInquiryListResponse, PlanInquiryListQueryKey>,
    'queryKey' | 'queryFn'
  >;
}

/**
 * 엔터프라이즈 문의 목록 조회 훅
 *
 * @description
 * - 검색, 필터, 페이지네이션 파라미터를 받아 문의 목록을 조회합니다.
 * - planInquiryApi.getPlanInquiries를 사용하여 실제 API를 호출합니다.
 */
export const usePlanInquiryList = (
  params: EnterpriseInquiryListParams,
  options: UsePlanInquiryListOptions = {}
): UseQueryResult<EnterpriseInquiryListResponse> => {
  const { queryOptions } = options;
  const { staleTime, ...restQueryOptions } = queryOptions ?? {};

  const queryKey = useMemo<PlanInquiryListQueryKey>(() => ['plan-inquiries', params] as const, [params]);

  return useQuery<EnterpriseInquiryListResponse, Error, EnterpriseInquiryListResponse, PlanInquiryListQueryKey>({
    queryKey,
    queryFn: async () => {
      const response = await planInquiryApi.getPlanInquiries(
        params.searchTerm,
        params.statusFilter,
        params.managerFilter,
        params.isNotAssigned,
        params.page,
        params.limit
      );

      const data = response.data.data;
      if (!data) {
        return {
          inquiries: [],
          total: 0,
          page: params.page,
          limit: params.limit,
        };
      }

      return {
        inquiries: data.data ?? [],
        total: data.count ?? 0,
        page: data.pageIndex ?? params.page,
        limit: params.limit,
      };
    },
    staleTime: staleTime ?? 30 * 1000,
    ...restQueryOptions,
  });
};

